import { BranchesOutlined, EditOutlined } from "@ant-design/icons";
import { Breadcrumb, Button, Col, Descriptions, Form, message, Modal, Row, Select, Table, Tooltip } from "antd";
import { DeleteButton } from "components/buttons";
import ContentWithHeader from "components/Layout/AppLayout/Content/ContentWithHeader";
import { RULE_REQUIRED } from "constants";
import useBajaAlta from "hooks/useBajaAlta";
import useColumnSearchServer from "hooks/useColumnSearchServer";
import useGetTramos from "hooks/useGetTramos";
import useLineasTramos from "hooks/useLineasTramos";
import useMutation from "hooks/useMutation";
import useRedirectIfInvalid from "hooks/useRedirectIfInvalidId;";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { filterOption, formatDuration, showErrorModal, sumarDuraciones } from "utils";
import Editar from "../Editar";
import useGetLinea from "../useGetLinea";
import { createLineaTramo, deleteAll } from "./api";
import TramosCarga from "./TramosCarga/index";
import ResumenRecorrido from "components/ResumenRecorrido";

const Tramos = () => {
  const location = useLocation();
  useRedirectIfInvalid(location.state);

  const [form] = Form.useForm()
  const { resetFields } = form;
  const { linea, fetchLinea } = useGetLinea(location.state?.linea?.id);
  const { fetch: fetchTramos, onTableChange, onSearch, data: lineasTramos, loading: loadingTramos } = useLineasTramos(linea?.id);
  const { tramos, fetchTramos: fetchTramosOption, loading: loadingTramosOption } = useGetTramos();
  const { getColumnSearchProps } = useColumnSearchServer(onSearch);
  const [selectedLineaTramo, setSelectedLineaTramo] = useState(null);
  const [openTramosCarga, setOpenTramosCarga] = useState(false);
  const [openEditar, setOpenEditar] = useState(false);
  const ultimoTramo = lineasTramos && lineasTramos[lineasTramos.length - 1];
  const duraciones = lineasTramos
    ?.filter(e => typeof e.duracion === "string" && e.duracion.includes(":"))
    .map(e => e.duracion);
  
  const duracionTotal = duraciones?.length ? sumarDuraciones(duraciones) : "";
  const kmTotales = tramos?.reduce((acc, tramo) => acc + (tramo.km || 0), 0);

  useEffect(() => {
    if (ultimoTramo?.destino) {
      fetchTramosOption(ultimoTramo?.destino);
    } else {
      fetchTramosOption();
    }
  }, [ultimoTramo?.destino]);

  const handleFetch = () => {
    fetchTramos(linea?.id);
  };

  const { bajaAlta } = useBajaAlta(() => handleFetch());

  const create = useMutation({
    mutationFn: createLineaTramo,
    onSuccess: () => {
      handleFetch();
      resetFields();
    },
    onError: err => showErrorModal({ err }),
  });

  const onFinish = (values) => {
    const body = {
      ...values,
      lineas_id: linea?.id,
      orden: !ultimoTramo ? 1 : ultimoTramo?.orden + 1,
    };
    create.mutate(body);
  };

  const eliminarTodo = useMutation({
    mutationFn: deleteAll,
    onSuccess: (res) => {
      handleFetch();
      fetchTramosOption();
      message.success(res.message);
    },
    onError: err => message.error(err.message),
  });

  const showDeleteAllConfirm = async () => {
    Modal.confirm({
      title: '¿Está seguro de que desea eliminar todos los tramos?',
      okText: 'SI',
      okType: 'danger',
      cancelText: 'NO',
      onOk() {
        eliminarTodo.mutate(linea?.id);
      },
    });
  };

  const breadcrumbItems = [
    {
      title: <Link to="/">Inicio</Link>,
    }, {
      title: <Link to="/lineas">Lineas</Link>,
    }, {
      title: linea?.id,
    },
  ];

  const columns = [
    {
      title: "Orden",
      dataIndex: "orden",
      sorter: true,
      width: 20,
      align: "center",
      ...getColumnSearchProps("orden", "input"),
    },
    {
      title: "Tramo",
      dataIndex: "tramo",
      sorter: true,
      width: 500,
      ...getColumnSearchProps("tramo", "input"),
    },
    {
      title: (lineasTramos?.length > 0 &&
        <DeleteButton
          title="Eliminar todos los tramos"
          size="small"
          onClick={() => showDeleteAllConfirm()}
        />
      ),
      align: "center",
      width: 100,
      render: (record, _, index) => {
        const isLast = index === lineasTramos.length - 1;
        return (
          <>
            <Tooltip title="Destino de Carga" mouseLeaveDelay={0}>
              <Button
                size="small"
                type="text"
                icon={<BranchesOutlined />}
                onClick={() => {
                  setOpenTramosCarga(true);
                  setSelectedLineaTramo(record);
                }}
              />
            </Tooltip>
            <DeleteButton
              title="Eliminar tramo"
              size="small"
              disabled={!isLast}
              onClick={() => bajaAlta("baja", "lineas_tramos", record.id)}
            />
          </>
        );
      },
    },
  ];

  return (
    <ContentWithHeader
      title={linea?.linea}
      rightContent={
        <Button
          icon={<EditOutlined />}
          onClick={() => setOpenEditar(true)}
        >
          Editar
        </Button>
      }
      breadcrumb={<Breadcrumb items={breadcrumbItems} />}
    >
      <Row justify="space-between" gutter={12}>
        <Col span={18}>
          <Form
            form={form}
            onFinish={onFinish}
            autoComplete="off"
            style={{ display: 'flex', gap: 12 }}
          >
            <Form.Item
              label="Tramo"
              name="tramos_id"
              rules={[RULE_REQUIRED]}
            >
              <Select
                options={tramos
                  ?.filter(tramo =>
                    !lineasTramos?.some(d => d.origen === tramo.origen || d.destino === tramo.destino)
                  )
                  ?.map(tramo => ({
                    value: tramo.id,
                    label: `${tramo.filial_origen} - ${tramo.filial_destino}`,
                  }))}
                showSearch
                filterOption={filterOption}
                style={{ width: 400, marginLeft: 12 }}
                placeholder="Seleccionar"
                loading={loadingTramosOption}
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={create.loading}
              >
                Agregar
              </Button>
            </Form.Item>
          </Form>
        </Col>
        <Col span={6}>
          <ResumenRecorrido
            tramos={lineasTramos}
          />
        </Col>
      </Row>
      <Table
        rowKey="id"
        size="small"
        pagination={false}
        onChange={onTableChange}
        dataSource={lineasTramos}
        columns={columns}
        loading={loadingTramos}
        style={{ marginTop: '8px' }}
        scroll={lineasTramos?.length > 8 && { y: 300 }}
        onRow={record => ({
          onDoubleClick: () => {
            setOpenTramosCarga(true);
            setSelectedLineaTramo(record);
          }
        })}
      />
      {openEditar && 
        <Editar
          selectedLinea={linea}
          onClose={() => {
            setOpenEditar(false);
            fetchLinea();
          }}
        />
      }
      {openTramosCarga &&
        <TramosCarga
          selectedTramoId={selectedLineaTramo?.tramos_id}
          selectedLineaTramo={selectedLineaTramo}
          onClose={() => {
            setOpenTramosCarga(false);
            handleFetch();
          }}
        />
      }
    </ContentWithHeader>
  );
}

export default Tramos;